import Plyr from 'plyr';

import { v4 as uuid } from 'uuid';
import { type App } from '~/app';

// eslint-disable-next-line import/no-unassigned-import
import '@root/node_modules/plyr/src/sass/plyr.scss';

export default (app: App) => {
    app.logger.debug('[Stockland FE] Initialise Stockland Video Component');

    const listeners: Array<[Element | Window | Document, string, EventListenerOrEventListenerObject]> = [];

    const playerlist: Plyr[] = [];

    app.afterMount
        .then(() => {
            mountVideo(listeners, playerlist);
        })
        .catch((error) => {
            throw new Error('[Stockland FE] Unable to register Video Component', error);
        });

    return function destroyVideo() {
        unMountVideo(listeners, playerlist);

        app.logger.debug('[DXA] Video Component destroyed');
    };
};

const mountVideo = (
    listeners: Array<[Element | Window | Document, string, EventListenerOrEventListenerObject]>,
    playerlist: Plyr[]
) => {
    const videoContainers = document.querySelectorAll<HTMLVideoElement>('.video-player-container');

    for (const videoContainer of videoContainers) {
        const autoPlay = !!videoContainer.dataset.autoPlay;
        const clickToPlay = !!videoContainer.dataset.clickToPlay;
        const closeIcon = videoContainer.dataset.closeButtonUrl ? videoContainer.dataset.closeButtonUrl : '';
        const closeButton = videoContainer.dataset.closeButton
            ? `<button type="button" class="plyr__control stopVideo"><svg class="icon w-8 h-8 stroke-1 stroke-white"><use xlink:href="${closeIcon}"></use></svg></button>`
            : '';

        let embedContainerClass="video-player-embed";

        const videoSource = videoContainer.dataset.source;
        const embedContainer = videoContainer.querySelector<HTMLElement>(`.${embedContainerClass}`);
        const htmlContainer = videoContainer.querySelector<HTMLElement>('.video-player-file');
        const videoPlayerElement = videoContainer.querySelector<HTMLVideoElement>('.video-player');

        let videoEmbed = false;
        let nativeVideoPoster = false;

        let isVimeo=videoSource?.includes('vimeo');

        const video = videoEmbed ? embedContainer : htmlContainer;
        const overlayPlayButton = videoContainer.querySelector<HTMLElement>('.overlay-play-btn');
        const customPlayButton = videoContainer.querySelector<HTMLElement>('.custom-play-btn');
        const customPlayButtonPlay = videoContainer.querySelector<HTMLElement>('.video-play-icon');
        const customPlayButtonPause = videoContainer.querySelector<HTMLElement>('.video-pause-icon');

        const isYoutubeVideo= videoSource?.includes('youtube') || videoSource?.includes('youtu.be');
        const applyPlyr= !isYoutubeVideo;

        if (embedContainer && htmlContainer) {
            if (isYoutubeVideo) {

                const ytFrame= document.createElement("iframe");
                ytFrame.setAttribute('anonymous','true');
                ytFrame.setAttribute('allow-fullscreen','true');
                embedContainer?.append(ytFrame)

                overlayPlayButton?.classList.remove('hidden');
                overlayPlayButton?.classList.add('z-1');

                const poster= document.createElement("img");
                poster.src= htmlContainer.dataset.poster||'';
                poster.alt = htmlContainer.dataset.alt ||'Video poster image';
                poster.classList.add('w-full','h-full','object-cover','absolute','top-0','left-0')
                videoContainer?.append(poster)

                videoContainer?.classList.add('relative')
                embedContainer.classList.remove('hidden')
                const beginAutoPlaySequence=()=>
                {
                    poster.classList.add('hidden');
                    overlayPlayButton?.classList.add('hidden');
                    ytFrame.src=videoSource+'?&autoplay=1&mute=1'
                }

                videoContainer.addEventListener('click', beginAutoPlaySequence)
                listeners.push([poster, "click",  beginAutoPlaySequence])
            } else if (isVimeo) {
                videoEmbed = true;
                //assigning the video container a unique class as a selector just in case there are multiple videos on the page
                embedContainerClass=`${embedContainerClass}${uuid()}`
                embedContainer.classList.add(embedContainerClass);
                embedContainer.dataset.plyrProvider = 'vimeo';
                htmlContainer.remove();

                if (videoSource) {
                    const id = getVimeoId(videoSource);
                    if (id) {
                        embedContainer.dataset.plyrEmbedId = id;
                    }
                }
            } else {
                embedContainer.remove();

                if (videoPlayerElement){
                    videoPlayerElement.addEventListener( "loadeddata", () => {
                        const videoWidth = videoPlayerElement.videoWidth;
                        const videoHeight = videoPlayerElement.videoHeight;
                        videoPlayerElement.style.aspectRatio = `${videoWidth}/${videoHeight}`;	
                    }, false );
                }

                if (htmlContainer?.dataset.poster) {
                    htmlContainer.setAttribute("poster", htmlContainer.dataset.poster);
                    nativeVideoPoster = true;
                }
            }
        }

        if (video && applyPlyr) {
            video.classList.toggle('hidden');

            const controls = `
			<div class="plyr__controls">
				<div class="plyr_controls_wrapper">
					<div class="plyr__controls_left">
						${closeButton}
						<button type="button" class="plyr__control playVideo" aria-label="Play, {title}" data-plyr="play">
							<svg class="icon--pressed" role="presentation"><use xlink:href="#plyr-pause"></use></svg>
							<svg class="icon--not-pressed" role="presentation"><use xlink:href="#plyr-play"></use></svg>
							<span class="label--pressed plyr__tooltip" role="tooltip">Pause</span>
							<span class="label--not-pressed plyr__tooltip" role="tooltip">Play</span>
						</button>
						<div class="volume_controls">
							<button type="button" class="plyr__control" aria-label="Mute" data-plyr="mute">
								<svg class="icon--pressed" role="presentation"><use xlink:href="#plyr-muted"></use></svg>
								<svg class="icon--not-pressed" role="presentation"><use xlink:href="#plyr-volume"></use></svg>
								<span class="label--pressed plyr__tooltip" role="tooltip">Unmute</span>
								<span class="label--not-pressed plyr__tooltip" role="tooltip">Mute</span>
							</button>
							<div class="plyr__volume">
								<input data-plyr="volume" type="range" min="0" max="1" step="0.05" value="1" autocomplete="off" aria-label="Volume">
							</div>
						</div>
					</div>

					<div class="plyr__progress">
						<input data-plyr="seek" type="range" min="0" max="100" step="0.01" value="0" aria-label="Seek">
						<progress class="plyr__progress__buffer" min="0" max="100" value="0">% buffered</progress>
						<span role="tooltip" class="plyr__tooltip">00:00</span>
					</div>

					<div class="plyr__controls_right">
						<button type="button" class="plyr__control" data-plyr="captions">
							<svg class="icon--pressed" role="presentation"><use xlink:href="#plyr-captions-on"></use></svg>
							<svg class="icon--not-pressed" role="presentation"><use xlink:href="#plyr-captions-off"></use></svg>
							<span class="label--pressed plyr__tooltip" role="tooltip">Disable captions</span>
							<span class="label--not-pressed plyr__tooltip" role="tooltip">Enable captions</span>
						</button>
						<button type="button" class="plyr__control" data-plyr="fullscreen">
							<svg class="icon--pressed" role="presentation"><use xlink:href="#plyr-exit-fullscreen"></use></svg>
							<svg class="icon--not-pressed" role="presentation"><use xlink:href="#plyr-enter-fullscreen"></use></svg>
							<span class="label--pressed plyr__tooltip" role="tooltip">Exit fullscreen</span>
							<span class="label--not-pressed plyr__tooltip" role="tooltip">Enter fullscreen</span>
						</button>
					</div>
				</div>
			</div>`;

            const videoPlayer = new Plyr(isVimeo?`.${embedContainerClass}`:video, {
                controls: controls,
                clickToPlay: clickToPlay,
                loop: { active: true },
                invertTime: false,
                hideControls: false,
                fullscreen: { enabled: true, fallback: true, iosNative: false } // Adding to test full screen behaviour on IOS
            });

            videoPlayer.on('error', (error) => {
                console.error('Plyr error', error);
            });

            if (autoPlay) {
                videoPlayer.muted = true;
                videoPlayer.volume = 0;
                htmlContainer?.setAttribute('playsinline', 'playsinline');
            }

            if (!nativeVideoPoster) {
                const videoPoster = video.dataset.poster;
                videoPlayer.poster = videoPoster ? videoPoster : '';
            }

            videoPlayer.currentTime = 0;

            // Don't let the volume get to 0 unless muted
            // Ensures mute button will always work
            const handleVolumeChange = (event: CustomEvent) => {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                const plyrDetails = event?.detail?.plyr;
                if (!plyrDetails.muted && plyrDetails.volume === 0) {
                    videoPlayer.volume = 0.05;
                }
            };

            videoPlayer.on('volumechange', handleVolumeChange);

            const hideButtons = () => {
                overlayPlayButton?.classList.add('hidden');
                videoContainer.classList.remove('hide-controls');
            };

            if (videoContainer.dataset.previewLength) {
                const preview = +videoContainer.dataset.previewLength;
                setTimeout(() => {
                    videoPlayer.pause();
                    overlayPlayButton?.classList.remove('hidden');
                    videoContainer.classList.add('cursor-pointer');
                    videoContainer.dataset.clickToPlay = 'false';
                }, preview);
            }

            if (videoContainer.dataset.overlayPlayButton) {
                overlayPlayButton?.classList.remove('hidden');
                videoContainer.classList.add('hide-controls');

                videoContainer.addEventListener('click', hideButtons);
                overlayPlayButton?.addEventListener('click', () => {
                    hideButtons();
                    void videoPlayer.play();
                });
            }

            if (videoContainer.dataset.customPlayButton) {
                customPlayButton?.classList.remove('hidden');
                videoContainer.classList.add('hide-controls');
                if(!autoPlay) {
                    customPlayButtonPlay?.classList.remove('hidden');
                    customPlayButtonPause?.classList.add('hidden');
                }
                
                customPlayButton?.addEventListener('click', () => {
                    if (videoPlayer.playing) {
                        videoPlayer.pause();
                        customPlayButtonPlay?.classList.remove('hidden');
                        customPlayButtonPause?.classList.add('hidden');
                    } else {
                        void videoPlayer.play();
                        customPlayButtonPlay?.classList.add('hidden');
                        customPlayButtonPause?.classList.remove('hidden');
                    }
                });
            }

            if(videoContainer.dataset.playOnHover){
                videoContainer.addEventListener('mouseenter', () => {
                    void videoPlayer.play();
                });
                videoContainer.addEventListener('mouseleave', () => {
                    videoPlayer.pause();
                });
                listeners.push([videoContainer, "mouseleave", () => {
                    videoPlayer.pause();
                }])
                listeners.push([videoContainer, "mouseenter", () => {
                    void videoPlayer.play();
                }]);
            }


            videoPlayer.currentTime = 0;

            playerlist.push(videoPlayer);
        }
    }
};

function unMountVideo(
    listeners: Array<[Element | Window | Document, string, EventListenerOrEventListenerObject]>,
    playerList: Plyr[]
) {
    for (const element of playerList) {
        element.destroy();
    }

    for (const [element, event, listener] of listeners) {
        element.removeEventListener(event, listener);
    }
}

function getYoutubeId(url: string) {
    const urlList = url.split(/(vi\/|v=|\/v\/|youtu\.be\/|\/embed\/)/);
    return urlList[2] === undefined ? urlList[0] : urlList[2].split(/[^\w\-]/i)[0];
}

function getVimeoId(url: string) {
    const urlList = url.split(/(videos|video|channels|\.com)\/(\d+)/);
    return urlList[2] === undefined ? urlList[0] : urlList[2].split(/[^\w\-]/i)[0];
}
